import React, { Fragment, memo, useCallback } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { Form, Formik } from 'formik'
import { Button } from 'src/components/shared/Button'
import { CommonModal } from 'src/components/shared/CommonModal'
import type { BasicSelectOption } from 'src/components/shared/form/BasicSelect'
import { FormikSelect } from 'src/components/shared/form/formik/FormikSelect'
import { partnerDriverDeletionReasonTitles } from 'src/constants/partnerDriver/constants'
import { PartnerDriverDeletionReason } from 'src/constants/partnerDriver/enums'
import type { PartnerDriverDetails } from 'src/interfaces/partnerDriver'
import { ContractorDeletionSummary } from './ContractorDeletionSummary'
import { deleteContractorFormValidator } from './DeleteContractorForm.validator'
import type { DeleteContractorFormValues } from './DeleteContractorForm.interface'

const FORM_ID = 'partner-driver-delete-contractor-form'

const INITIAL_VALUES: DeleteContractorFormValues = { reason: '' }

const REASON_OPTIONS: BasicSelectOption[] = Object.values(PartnerDriverDeletionReason).map(
  (reason) => {
    return { label: partnerDriverDeletionReasonTitles[reason], value: reason }
  },
)

interface Props {
  isOpen: boolean
  driver: PartnerDriverDetails
  isDeleting: boolean
  onConfirm(reason: PartnerDriverDeletionReason): void
  onClose(): void
}

/** ENG-1214 — Retool's "Delete contractor" confirmation, with the reason the backend records. */
const DeleteContractorModalComponent = ({
  isOpen,
  driver,
  isDeleting,
  onConfirm,
  onClose,
}: Props) => {
  const handleSubmit = useCallback(
    (values: DeleteContractorFormValues) => {
      // The validator rejects '' before Formik calls submit; this narrows the type.
      if (values.reason === '') {
        return
      }

      onConfirm(values.reason)
    },
    [onConfirm],
  )

  return (
    <Formik<DeleteContractorFormValues>
      initialValues={INITIAL_VALUES}
      validationSchema={deleteContractorFormValidator}
      onSubmit={handleSubmit}
    >
      {({ values }) => {
        return (
          <CommonModal
            isOpen={isOpen}
            title="Delete this contractor?"
            className={tw`max-w-[560px]`}
            onClose={onClose}
            buttons={
              <Fragment>
                <Button size="small" color="grey" disabled={isDeleting} onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  size="small"
                  color="red"
                  type="submit"
                  form={FORM_ID}
                  loading={isDeleting}
                  disabled={isDeleting || !values.reason}
                  data-testid="partner-driver-delete-contractor-confirm-button"
                >
                  Delete contractor
                </Button>
              </Fragment>
            }
          >
            <Form id={FORM_ID} css={tw`flex flex-col gap-4`}>
              <Typography variant="body2">
                The contractor's data will be permanently deleted. This cannot be undone.
              </Typography>
              <ContractorDeletionSummary driver={driver} />
              <FormikSelect<DeleteContractorFormValues>
                name="reason"
                label="Reason *"
                options={REASON_OPTIONS}
                placeholder="Choose a reason"
                inputProps={{ 'data-testid': 'partner-driver-delete-contractor-reason-input' }}
              />
            </Form>
          </CommonModal>
        )
      }}
    </Formik>
  )
}

export const DeleteContractorModal = memo(DeleteContractorModalComponent)
